import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { errorToast, successToast } from "../../../redux/actionCreator";
import {
  createUpdate,
  deleteUpdate,
  getItem,
  listActivity,
  listUpdates,
  setCellValue,
  updateItem,
  uploadInlineAttachment,
} from "../api/boardApi";
import { CellRenderer } from "./cells";
import MentionText from "./MentionText";
import MentionInput, { resolveMentions } from "./MentionInput";
import { getSession } from "../../../utils/session";
import useBoardWs from "../useBoardWs";

const formatTs = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ItemPanel({ board, item, peopleIndex, allPeople, readOnly, onClose, onItemChange }) {
  const [full, setFull] = useState(item);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [tab, setTab] = useState("updates");
  const [draft, setDraft] = useState("");
  const [titleDraft, setTitleDraft] = useState(item?.title || "");
  const [posting, setPosting] = useState(false);
  const me = getSession() || {};

  useEffect(() => {
    setFull(item);
    setTitleDraft(item?.title || "");
  }, [item?._id]);

  useEffect(() => {
    let alive = true;
    if (!item?._id) return undefined;
    setActivityLoaded(false);
    setTab("updates");
    (async () => {
      try {
        const [itemRes, updatesRes] = await Promise.all([
          getItem(board._id, item._id),
          listUpdates(board._id, item._id),
        ]);
        if (!alive) return;
        setFull(itemRes.data);
        setComments(updatesRes.data || []);
      } catch (e) {
        if (alive) errorToast(e.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [board._id, item?._id]);

  // Lazy-load activity once user switches to the tab.
  useEffect(() => {
    if (tab !== "activity" || activityLoaded || !full?._id) return;
    (async () => {
      try {
        const res = await listActivity(board._id, { item_id: full._id, limit: 100 });
        setActivity(res.data || []);
        setActivityLoaded(true);
      } catch (e) {
        errorToast(e.message);
      }
    })();
  }, [tab, activityLoaded, board._id, full?._id]);

  // Live updates for THIS item's comments + activity, scoped to the open panel.
  useBoardWs(board._id, (event, payload) => {
    if (!full?._id || !payload) return;
    if (event === "update.added" && payload.update?.item_id === full._id) {
      setComments((cs) => (cs.some((c) => c._id === payload.update._id) ? cs : [payload.update, ...cs]));
    }
    if (event === "update.deleted" && String(payload.item_id) === String(full._id)) {
      setComments((cs) => cs.filter((c) => c._id !== payload.update_id));
    }
    // Any event tied to this item belongs in the activity feed.
    if (payload.item_id && String(payload.item_id) === String(full._id) && activityLoaded) {
      setActivity((arr) => [
        {
          _id: payload.activity_id || `tmp-${Date.now()}`,
          board_id: board._id,
          item_id: full._id,
          actor: payload.actor,
          kind: event,
          payload,
          createdAt: payload.createdAt || new Date().toISOString(),
        },
        ...arr,
      ]);
    }
  });

  const commitCell = async (colId, value) => {
    try {
      const res = await setCellValue(board._id, full._id, colId, value);
      const next = {
        ...full,
        column_values: {
          ...(full.column_values || {}),
          [colId]: {
            value: res.data.value,
            type: res.data.type,
            updated_by: res.data.updated_by,
            updated_at: res.data.updated_at,
          },
        },
      };
      setFull(next);
      onItemChange && onItemChange(next);
    } catch (e) {
      errorToast(e.message);
    }
  };

  const commitTitle = async () => {
    const next = titleDraft.trim();
    if (!next || next === full.title) return;
    try {
      const res = await updateItem(board._id, full._id, { title: next });
      setFull((f) => ({ ...f, title: res.data.title }));
      onItemChange && onItemChange({ ...full, title: res.data.title });
    } catch (e) {
      errorToast(e.message);
      setTitleDraft(full.title);
    }
  };

  const post = async () => {
    const display = draft.trim();
    if (!display) return;
    // The textarea contains "@DisplayName" tokens; convert to "@<id>" for
    // the backend so notifications/mentions fan out correctly.
    const body = resolveMentions(display, allPeople);
    setPosting(true);
    try {
      const res = await createUpdate(board._id, full._id, body);
      setComments((cs) => [res.data, ...cs]);
      setDraft("");
    } catch (e) {
      errorToast(e.message);
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (updateId) => {
    try {
      await deleteUpdate(board._id, full._id, updateId);
      setComments((cs) => cs.filter((c) => c._id !== updateId));
      successToast("Comment deleted");
    } catch (e) {
      errorToast(e.message);
    }
  };

  if (!full) return null;

  return (
    <>
      <div className="item-panel-backdrop" onClick={onClose} />
      <aside className="item-panel">
        <header className="item-panel-head">
          <input
            className="item-panel-title"
            value={titleDraft}
            disabled={readOnly}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
            }}
          />
          <button className="icon-btn" onClick={onClose} title="Close" aria-label="Close"><X size={14} /></button>
        </header>

        <section className="item-panel-section">
          <div className="section-label">Columns</div>
          {board.columns
            .slice()
            .sort((a, z) => a.order - z.order)
            .map((col) => (
              <div key={col._id} className="item-panel-field">
                <div className="field-label">{col.title}</div>
                <div className="field-value">
                  <CellRenderer
                    column={col}
                    value={full.column_values?.[col._id]?.value ?? null}
                    readOnly={readOnly}
                    peopleIndex={peopleIndex}
                    allPeople={allPeople}
                    item={full}
                    boardId={board._id}
                    onCommit={(v) => commitCell(col._id, v)}
                  />
                </div>
              </div>
            ))}
        </section>

        <section className="item-panel-section updates">
          <div className="tabs">
            <button
              type="button"
              className={`tab ${tab === "updates" ? "active" : ""}`}
              onClick={() => setTab("updates")}
            >
              Updates
            </button>
            <button
              type="button"
              className={`tab ${tab === "activity" ? "active" : ""}`}
              onClick={() => setTab("activity")}
            >
              Activity
            </button>
          </div>

          {tab === "updates" && (
            <>
              <div className="update-composer">
                <MentionInput
                  value={draft}
                  onChange={setDraft}
                  placeholder="Write an update... type @ to mention, paste or drop an image"
                  rows={3}
                  employees={allPeople}
                  onSubmit={post}
                  onUpload={(file) => uploadInlineAttachment(board._id, full._id, file).then((r) => r.data)}
                />
                <button
                  className="btn-primary"
                  disabled={posting || !draft.trim()}
                  onClick={post}
                >
                  {posting ? "Posting…" : "Post"}
                </button>
              </div>
              <div className="updates-list">
                {comments.length === 0 && <div className="updates-empty">No updates yet</div>}
                {comments.map((c) => {
                  const author = peopleIndex[c.author];
                  const name = author
                    ? `${author.firstname || ""} ${author.lastname || ""}`.trim() || author.email
                    : c.author;
                  const mine = String(c.author) === String(me._id);
                  return (
                    <div key={c._id} className="update-row">
                      <div className="update-meta">
                        <strong>{name}</strong>
                        <span className="update-ts">{formatTs(c.createdAt)}</span>
                        {mine && (
                          <button
                            className="update-del"
                            onClick={() => removeComment(c._id)}
                            title="Delete"
                            aria-label="Delete comment"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <div className="update-body">
                        <MentionText body={c.body} peopleIndex={peopleIndex} boardId={board._id} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {tab === "activity" && (
            <ActivityFeed activity={activity} peopleIndex={peopleIndex} loaded={activityLoaded} />
          )}
        </section>
      </aside>
    </>
  );
}

function ActivityFeed({ activity, peopleIndex, loaded }) {
  if (!loaded) return <div className="updates-empty">Loading…</div>;
  if (!activity?.length) return <div className="updates-empty">No activity yet</div>;
  return (
    <div className="activity-list">
      {activity.map((a) => {
        const author = peopleIndex[a.actor];
        const name = author
          ? `${author.firstname || ""} ${author.lastname || ""}`.trim() || author.email
          : a.actor;
        return (
          <div key={a._id} className="activity-row">
            <span className="activity-actor">{name}</span>{" "}
            <span className="activity-kind">{describeActivity(a)}</span>
            <span className="activity-ts">{formatTs(a.createdAt)}</span>
          </div>
        );
      })}
    </div>
  );
}

function describeActivity(a) {
  const k = a.kind;
  if (k === "item.created") return "created this item";
  if (k === "item.updated") return "updated item details";
  if (k === "item.deleted") return "deleted the item";
  if (k === "cell.changed") return `changed a cell value`;
  if (k === "update.added") return "posted an update";
  if (k === "update.deleted") return "deleted an update";
  if (k === "column.created") return "added a column";
  if (k === "column.updated") return "updated a column";
  if (k === "column.deleted") return "removed a column";
  if (k === "group.created") return "added a group";
  if (k === "group.updated") return "updated a group";
  if (k === "group.deleted") return "removed a group";
  if (k === "board.updated") return "updated board settings";
  if (k === "member.added") return "added a member";
  if (k === "member.removed") return "removed a member";
  return k;
}
